import { forwardRef, type PropsWithChildren, useRef } from 'react';

import { TimeseriesAnnotationsIndicators } from './components/annotations-timeseries/TimeseriesAnnotationsIndicators.js';
import { TimeseriesChartAnnotationsArea } from './components/annotations-timeseries/TimeseriesChartAnnotationsArea.js';
import { TimeseriesTimeBottomAxisArea } from './components/axes/TimeseriesTimeBottomAxisArea/TimeseriesTimeBottomAxisArea.js';
import { TimeseriesValueLeftAxisArea } from './components/axes/TimeseriesValueLeftAxisArea.js';
import { TimeseriesValueRightAxisArea } from './components/axes/TimeseriesValueRightAxisArea.js';
import { TimeseriesChartOverlays } from './components/overlays/TimeseriesChartOverlays.js';
import { ThresholdIndicators } from './components/thresholds/ThresholdIndicators.js';
import { TimeseriesThresholdArea } from './components/thresholds/TimeseriesThresholdArea.js';
import { DISPLAY_NAME, TIMESERIES_ARIA_LABEL } from './constants.js';
import { useAnnotationsConfig } from './hooks/useAnnotationsConfig.js';
import { useChartInteractions } from './hooks/useChartInteractions.js';
import { useTimeseriesFilteredData } from './hooks/useTimeseriesFilteredData.js';
import { useTimeseriesFilteredYAxis } from './hooks/useTimeseriesFilteredYAxis.js';
import { useTimeseriesLegend } from './hooks/useTimeseriesLegend.js';
import { TimeseriesChartPlot } from './layout/TimeseriesChartPlot.js';
import { TimeseriesChartImperativeHandler } from './providers/TimeseriesChartImperativeHandler.js';
import { TimeseriesChartProviders } from './providers/TimeseriesChartProviders.js';
import type { TimeseriesChartConfiguration } from './types/timeseries-config.js';
import { AREAS } from './types/timeseries-grid-area-rules.js';
import type { TimeseriesChartRef } from './types/timeseries-ref.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { SCROLLBAR_WIDTH } from '../core/components/annotations/constants.js';
import { getAnnotationsVisibleArea } from '../core/components/annotations/utils/get-annotations-visible-area.js';
import { ChartGrid } from '../core/components/chart-grid/ChartGridRoot.js';
import {
  shouldShowAnnotations,
  shouldShowAxes,
} from '../core/components/chart-grid/constants.js';
import { useChartLayoutLoading } from '../core/components/chart-layout/hooks/useChartLayoutLoading.js';
import { useChartLayoutOrientation } from '../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { useGraphSize } from '../core/components/chart-layout/hooks/useGraphSize.js';
import { ChartLoaderOverlay } from '../core/components/chart-loader-overlay/ChartLoaderOverlay.js';
import {
  BOTTOM_PADDING_ADJUSTMENT,
  OVERLAY_PADDING,
} from '../core/components/data-point/constants.js';
import { useAriaLabelingContext } from '../core/context/AriaLabelingContext.js';

export function computeChartPadding(
  axisLeftConfig: unknown,
  axisRightConfig: unknown,
  unitsCount: number,
  isLegendHiddenOrVertical: boolean,
): { hasLeftPadding: boolean; hasRightPadding: boolean } {
  const singleUnit = unitsCount === 1 && isLegendHiddenOrVertical;
  const paddingRight = Boolean(
    (!axisLeftConfig && !axisRightConfig && singleUnit) ||
      (axisLeftConfig && axisRightConfig && singleUnit) ||
      (axisLeftConfig && isLegendHiddenOrVertical),
  );
  const onlyRightAxis = !axisLeftConfig && Boolean(axisRightConfig);
  return {
    hasLeftPadding: onlyRightAxis,
    hasRightPadding: onlyRightAxis || paddingRight,
  };
}

const timeseriesGridTemplateAreas = `
  "${AREAS.annotations} ${AREAS.annotations} ${AREAS.annotations} . ."
  "${AREAS.leftAxis} ${AREAS.leftThreshold} plot ${AREAS.rightThreshold} ${AREAS.rightAxis}"
  ". . ${AREAS.bottomAxis} . ."
`;
const timeseriesGridTemplateColumns =
  'minmax(0, auto) auto minmax(60%, 1fr) auto minmax(0, auto)';

export interface _TimeseriesChartProps {
  config: TimeseriesChartConfiguration;
}

export const TimeseriesChartInternal = /* @__PURE__ */ forwardRef<
  TimeseriesChartRef,
  PropsWithChildren<_TimeseriesChartProps>
>(({ config }, forwardedRef) => {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = useAriaLabelingContext();
  const loading = useChartLayoutLoading();
  const annotations = useAnnotationsConfig();
  const tracksQuantity = annotations.tracks.length;
  const numHiddenTracks = annotations.tracks.filter(
    (value) => value.hidden,
  ).length;

  const chartRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs<TimeseriesChartRef | HTMLDivElement>([
    forwardedRef,
    chartRef,
  ]);

  const { width, height } = useGraphSize();
  const { orientation } = useChartLayoutOrientation();
  const { right: axisRightConfig, left: axisLeftConfig } =
    useTimeseriesFilteredYAxis();
  const { units } = useTimeseriesFilteredData();
  const { hidden: legendHidden } = useTimeseriesLegend();

  const annotationsVisibleArea = getAnnotationsVisibleArea(
    tracksQuantity,
    annotations.visibleTracksLimit,
    numHiddenTracks,
  );

  const availableWidth =
    tracksQuantity && orientation === 'vertical'
      ? width - SCROLLBAR_WIDTH
      : width;

  const isLegendHiddenOrVertical = orientation === 'vertical' || legendHidden;
  const isLegendHiddenOrRight = orientation === 'horizontal' || legendHidden;

  const { hasLeftPadding, hasRightPadding } = computeChartPadding(
    axisLeftConfig,
    axisRightConfig,
    units.length,
    isLegendHiddenOrVertical,
  );

  const chartGridHeight =
    height -
    (!tracksQuantity || isLegendHiddenOrRight
      ? OVERLAY_PADDING + BOTTOM_PADDING_ADJUSTMENT
      : 0);

  const showAnnotations = shouldShowAnnotations(
    chartGridHeight,
    tracksQuantity,
  );

  const showAxes = shouldShowAxes(chartGridHeight);
  const chartGridRenderHeight =
    tracksQuantity && !isLegendHiddenOrRight && !showAnnotations
      ? chartGridHeight - OVERLAY_PADDING
      : chartGridHeight;

  const annotationsRowHeight = showAnnotations ? annotationsVisibleArea : 0;

  const timeseriesGridTemplateRows = showAxes
    ? `minmax(0, ${annotationsRowHeight}px) minmax(60%, 1fr) minmax(0, auto)`
    : '0px 1fr 1px';

  useChartInteractions();

  if (!height || !width) {
    return null;
  }

  return (
    <div
      style={{
        paddingTop: showAnnotations ? 0 : OVERLAY_PADDING,
        paddingLeft: hasLeftPadding ? OVERLAY_PADDING : 0,
      }}
      className={defaultChartSize}
      aria-label={ariaLabel ?? TIMESERIES_ARIA_LABEL}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-details={ariaDetails}
      role="img"
    >
      <ChartGrid
        width={availableWidth - (hasRightPadding ? OVERLAY_PADDING : 0)}
        height={chartGridRenderHeight}
        gridTemplateAreas={timeseriesGridTemplateAreas}
        gridTemplateColumns={timeseriesGridTemplateColumns}
        gridTemplateRows={timeseriesGridTemplateRows}
        ref={mergedRef}
      >
        <TimeseriesChartProviders>
          <TimeseriesChartImperativeHandler
            config={config}
            forwardedRef={forwardedRef}
            chartRef={chartRef}
          >
            {showAxes && <TimeseriesValueLeftAxisArea />}
            {showAxes && <TimeseriesTimeBottomAxisArea />}

            <ChartGrid.PlotArea>
              <TimeseriesChartPlot />
              {showAnnotations && <TimeseriesAnnotationsIndicators />}
              <ChartLoaderOverlay
                loading={!!loading}
                zoomEnabled={!!config.interactions?.zoom?.enabled}
              >
                <TimeseriesChartOverlays />
              </ChartLoaderOverlay>

              <ThresholdIndicators indicatorType="line" />
            </ChartGrid.PlotArea>

            {showAxes && <TimeseriesValueRightAxisArea />}
            <TimeseriesThresholdArea />

            {showAnnotations && <TimeseriesChartAnnotationsArea />}
          </TimeseriesChartImperativeHandler>
        </TimeseriesChartProviders>
      </ChartGrid>
    </div>
  );
});

(
  TimeseriesChartInternal as typeof TimeseriesChartInternal & {
    displayName: string;
  }
).displayName = DISPLAY_NAME;
